import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CheckCircle, AlertTriangle } from "react-feather";
import agent from "../../utils/axios";
import { Authenticate } from "../../types/Authenticate";
import { QuickBooksCompany } from "../bills/CompanySelectDialog";

interface AuthUrlResponse {
  url: string;
}

/**
 * Admin screen to authorize each configured QuickBooks company once, so CSIPay holds its
 * own access token per RealmId. After a company is connected, Convert to Check routes bills
 * to it using the stored token (no per-convert popup). CSIPay owns these tokens — it never
 * borrows COMPASS's.
 */
function ConnectQuickBooksCompanies() {
  const [companies, setCompanies] = useState<QuickBooksCompany[]>([]);
  const [loading, setLoading] = useState(false);
  const [connectingRealmId, setConnectingRealmId] = useState<string | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result: QuickBooksCompany[] = await agent.quickbooks.getCompanies();
      setCompanies(result);
    } catch (e) {
      setError("Unable to load QuickBooks companies.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  // Exchange the OAuth code for a token stored under the company's RealmId, then refresh.
  const handleAuthCode = useCallback(
    async (code: string | null, redirect_uri: string, realmId: string) => {
      if (!code) {
        setConnectingRealmId(null);
        return;
      }
      try {
        const authRequest: Authenticate = { code };
        await agent.quickbooks.authenticateQuickbooks(
          authRequest,
          redirect_uri,
          realmId
        );
        setMessage("QuickBooks company connected successfully.");
        await loadCompanies();
      } catch (e) {
        setError("Failed to connect the QuickBooks company. Please try again.");
      } finally {
        setConnectingRealmId(null);
      }
    },
    [loadCompanies]
  );

  // Open the QuickBooks sign-in popup for a specific company (RealmId).
  const connectCompany = useCallback(
    async (realmId: string) => {
      setError(null);
      setMessage(null);
      setConnectingRealmId(realmId);
      try {
        const { url }: AuthUrlResponse = await agent.quickbooks.getAuthUrl();
        if (!url) {
          setConnectingRealmId(null);
          return;
        }
        const urlParams = new URLSearchParams(new URL(url).search);
        const redirect_uri = urlParams.get("redirect_uri") ?? "";
        const popupWidth = 600;
        const popupHeight = 700;
        const left = window.screen.width / 2 - popupWidth / 2;
        const top = window.screen.height / 2 - popupHeight / 2;

        const authWindow = window.open(
          url,
          "QuickBooks Login",
          `width=${popupWidth},height=${popupHeight},left=${left},top=${top}`
        );

        if (!authWindow) {
          setConnectingRealmId(null);
          return;
        }

        const authCheckInterval = setInterval(() => {
          try {
            if (authWindow.location.href.includes("code=")) {
              const params = new URLSearchParams(authWindow.location.search);
              const code = params.get("code");
              if (code) {
                clearInterval(authCheckInterval);
                authWindow.close();
                // Use the company the admin chose to connect (not the callback realm).
                handleAuthCode(code, redirect_uri, realmId);
              }
            }
          } catch (err) {
            // Ignore cross-origin errors until the popup redirects back to our domain.
          }
          if (authWindow.closed) {
            clearInterval(authCheckInterval);
            setConnectingRealmId((current) =>
              current === realmId ? null : current
            );
          }
        }, 200);
      } catch (e) {
        setError("Unable to start QuickBooks sign-in.");
        setConnectingRealmId(null);
      }
    },
    [handleAuthCode]
  );

  const renderStatus = (company: QuickBooksCompany) => {
    // hasValidToken == a non-expired refresh token exists, so the company is usable
    // (the convert flow auto-refreshes an expired access token). Everything else needs sign-in.
    if (company.hasValidToken) {
      return (
        <Chip
          size="small"
          color="success"
          icon={<CheckCircle size={14} />}
          label="Connected"
        />
      );
    }
    return (
      <Chip
        size="small"
        color="default"
        icon={<AlertTriangle size={14} />}
        label="Not connected"
      />
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h3" gutterBottom>
        Connect QuickBooks Companies
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Authorize each QuickBooks company once. CSIPay stores its own access
        token for that company, so Convert to Check can send bills to the
        correct company without a sign-in popup each time. When connecting, sign
        in to the QuickBooks company that matches the row you clicked.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setMessage(null)}
        >
          {message}
        </Alert>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading companies…</Typography>
            </Box>
          ) : companies.length === 0 ? (
            <Typography variant="body2">
              No QuickBooks companies are configured for this environment.
            </Typography>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {companies.map((company, index) => (
                  <TableRow key={`${company.realmId}-${index}`}>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {company.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        RealmId: {company.realmId}
                      </Typography>
                    </TableCell>
                    <TableCell>{renderStatus(company)}</TableCell>
                    <TableCell align="right">
                      <Button
                        variant="contained"
                        size="small"
                        disabled={connectingRealmId !== null}
                        onClick={() => connectCompany(company.realmId)}
                        startIcon={
                          connectingRealmId === company.realmId ? (
                            <CircularProgress size={14} color="inherit" />
                          ) : undefined
                        }
                      >
                        {company.hasValidToken ? "Re-connect" : "Connect"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          <Divider sx={{ my: 2 }} />
          <Button variant="outlined" size="small" onClick={loadCompanies}>
            Refresh
          </Button>
        </CardContent>
      </Card>
    </Box>
  );
}

export default ConnectQuickBooksCompanies;
